class CollectionGridSearch {
  constructor(element) {
    this.element = element;
    this.input = element.querySelector('[data-collection-search-input]');
    this.clearButton = element.querySelector('[data-collection-search-clear]');
    this.status = element.querySelector('[data-collection-search-status]');
    this.sectionId = element.dataset.sectionId;
    this.requestVersion = 0;
    this.ignoreMutations = false;
    this.captureGrid();

    this.input.addEventListener('input', () => this.handleInput());
    this.clearButton.addEventListener('click', () => this.clear());

    const container = document.getElementById('ProductGridContainer');
    if (container) {
      this.observer = new MutationObserver(() => this.handleGridUpdate());
      this.observer.observe(container, { childList: true, subtree: true });
    }
  }

  get grid() {
    return document.getElementById('product-grid');
  }

  get pagination() {
    return document.querySelector('#ProductGridContainer .pagination-wrapper');
  }

  captureGrid() {
    if (!this.grid) return;

    this.originalGrid = this.grid.innerHTML;
    this.originalPagination = this.pagination ? this.pagination.outerHTML : '';
  }

  handleInput() {
    window.clearTimeout(this.searchTimer);
    const hasQuery = this.input.value.trim().length > 0;
    this.clearButton.classList.toggle('hidden', !hasQuery);

    if (!hasQuery) {
      this.restoreGrid();
      return;
    }

    this.searchTimer = window.setTimeout(() => this.filterGrid(), 250);
  }

  handleGridUpdate() {
    if (this.ignoreMutations) return;

    if (this.input.value.trim()) {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => this.filterGrid(), 50);
    } else {
      this.captureGrid();
    }
  }

  clear() {
    this.input.value = '';
    this.input.focus();
    this.clearButton.classList.add('hidden');
    this.restoreGrid();
  }

  restoreGrid() {
    this.requestVersion += 1;
    if (!this.grid || this.originalGrid === undefined) return;

    this.setIgnoringMutations(() => {
      this.grid.innerHTML = this.originalGrid;
      const pagination = this.pagination;
      if (pagination) pagination.remove();
      if (this.originalPagination) {
        this.grid.closest('.collection').insertAdjacentHTML('beforeend', this.originalPagination);
      }
    });
    this.status.textContent = '';
  }

  getPageCount() {
    let pageCount = 1;
    document.querySelectorAll('#ProductGridContainer .pagination a[href]').forEach((link) => {
      const page = Number(new URL(link.href).searchParams.get('page'));
      if (Number.isFinite(page)) pageCount = Math.max(pageCount, page);
    });
    return Math.min(pageCount, 20);
  }

  async getProductsForPage(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', this.sectionId);
    url.searchParams.set('page', page);

    const response = await fetch(url.toString());
    if (!response.ok) throw new Error(`Collection page request failed: ${response.status}`);

    const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    return [...documentFragment.querySelectorAll('#product-grid > [data-collection-product]')];
  }

  async filterGrid() {
    const query = this.input.value.trim().toLocaleLowerCase();
    if (!query || !this.grid) return;

    const requestVersion = ++this.requestVersion;
    const pageCount = this.getPageCount();
    this.element.classList.add('is-loading');
    this.status.textContent = 'Filtering this collection';

    try {
      const pages = await Promise.all(
        Array.from({ length: pageCount }, (_, index) => this.getProductsForPage(index + 1)),
      );
      if (requestVersion !== this.requestVersion) return;

      const products = pages.flat().filter((product) => {
        return (product.dataset.productTitle || '').toLocaleLowerCase().includes(query);
      });

      this.setIgnoringMutations(() => {
        this.grid.replaceChildren(
          ...(products.length
            ? products.map((product) => {
                const result = product.cloneNode(true);
                result.classList.remove('animate--slide-in');
                result.removeAttribute('data-cascade');
                result.style.removeProperty('--animation-order');
                return result;
              })
            : [this.emptyResult()]),
        );
        if (this.pagination) this.pagination.hidden = true;
      });

      this.status.textContent = `${products.length} ${products.length === 1 ? 'match' : 'matches'} in this collection`;
    } catch (error) {
      console.error(error);
      this.status.textContent = 'Unable to filter this collection. Please try again.';
    } finally {
      if (requestVersion === this.requestVersion) this.element.classList.remove('is-loading');
    }
  }

  emptyResult() {
    const item = document.createElement('li');
    item.className = 'collection-search-empty';
    item.textContent = `No products in this collection match “${this.input.value.trim()}”.`;
    return item;
  }

  setIgnoringMutations(callback) {
    this.ignoreMutations = true;
    callback();
    window.setTimeout(() => {
      this.ignoreMutations = false;
    });
  }
}

document.querySelectorAll('[data-collection-grid-search]').forEach((element) => {
  new CollectionGridSearch(element);
});
